import { Component, DestroyRef, inject, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AccountLoginService, ExternalProvider, LoginError } from './account-login.service';
import { HOST_BRANDING, LoginBrand, loginBrandFor, productBrandFor, SERVED_HOSTNAME } from './product-brand';

/**
 * The SPA's own login page. AuthServer's cookie middleware redirects an unauthenticated core-web-spa
 * /connect/authorize challenge here (OnRedirectToLogin in AuthServer/Program.cs) instead of to the
 * Razor login page, so the whole sign-in experience stays inside the Angular app.
 *
 * Flow: collect credentials → POST them to /auth/api/account/login (which sets the shared "AuthServer"
 * SSO cookie via SignInManager) → then RE-START the OIDC flow with oidc.authorize(). That second
 * /connect/authorize now finds the cookie and issues a code, so /callback completes the PKCE exchange
 * normally. The password never touches the token endpoint — this is not the password grant.
 *
 * Must NOT be guarded (registered outside MainLayoutComponent, like /callback): the user is
 * unauthenticated by definition when they land here, so guarding it would just bounce back to
 * /connect/authorize → OnRedirectToLogin → here again, an infinite loop.
 *
 * The deep link the user was originally aiming at is preserved by the guard in sessionStorage
 * (RETURN_URL_KEY) and restored by /callback — so a fresh oidc.authorize() here loses nothing.
 */
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [RouterLink],
  template: `
    <div class="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div class="w-full max-w-sm rounded-lg border border-gray-200 bg-white p-8 shadow-sm">
        @if (brand()?.logo; as logo) {
          <!-- Decorative: the heading below names whose sign-in this is, so a screen reader hears it
               once. Height fixed up front so the form does not jump when the image arrives. -->
          <img [src]="logo" alt="" height="56" class="mx-auto mb-4 block h-14 w-auto max-w-full" />
        }
        <h1 class="mb-6 text-center text-xl font-semibold text-gray-800">
          Sign in{{ brand() ? ' to ' + brand()!.name : '' }}
        </h1>

        <form (submit)="submit($event)">
          <label class="mb-1 block text-sm font-medium text-gray-700" for="username"
            >Username</label
          >
          <input
            id="username"
            name="username"
            type="text"
            autocomplete="username"
            required
            [value]="username()"
            (input)="username.set($any($event.target).value)"
            class="mb-4 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
          />

          <label class="mb-1 block text-sm font-medium text-gray-700" for="password"
            >Password</label
          >
          <div class="relative mb-4">
            <input
              #passwordInput
              id="password"
              name="password"
              [type]="showPassword() ? 'text' : 'password'"
              autocomplete="current-password"
              required
              [value]="password()"
              (input)="password.set($any($event.target).value)"
              class="w-full rounded-md border border-gray-300 py-2 pl-3 pr-16 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              type="button"
              (click)="togglePassword(passwordInput)"
              [attr.aria-label]="showPassword() ? 'Hide password' : 'Show password'"
              [attr.aria-pressed]="showPassword()"
              class="absolute inset-y-0 right-0 px-3 text-xs font-medium text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {{ showPassword() ? 'Hide' : 'Show' }}
            </button>
          </div>

          <label class="mb-4 flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              [checked]="rememberMe()"
              (change)="rememberMe.set($any($event.target).checked)"
            />
            Remember me
          </label>

          @if (errorMessage()) {
            <p class="mb-4 text-sm text-red-600">{{ errorMessage() }}</p>
          }

          <button
            type="submit"
            [disabled]="submitting()"
            class="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-60"
          >
            {{ submitting() ? 'Signing in…' : 'Sign in' }}
          </button>
        </form>

        <!-- routerLink, not href: a full navigation would drop the PKCE verifier/state the oidc
             library just persisted for the authorize URL this page may still resume. -->
        <p class="mt-4 text-center text-sm">
          <a routerLink="/forgot-password" class="font-medium text-primary hover:underline">
            Forgot your password?
          </a>
        </p>

        @if (brand()?.selfRegistration !== false) {
          <p class="mt-2 text-center text-sm text-gray-500">
            Don't have an account?
            <a routerLink="/register" class="font-medium text-primary hover:underline">Register</a>
          </p>
        }

        @if (providers().length > 0) {
          <div class="my-6 flex items-center gap-3 text-xs text-gray-400">
            <span class="h-px flex-1 bg-gray-200"></span>OR<span
              class="h-px flex-1 bg-gray-200"
            ></span>
          </div>

          <!-- External login is a top-level form POST to the MVC AccountController.ExternalLogin, which
               is antiforgery-protected: the same request token doubles as the __RequestVerificationToken
               field. A full-page navigation is what lets the provider redirect flow run. -->
          @for (provider of providers(); track provider.name) {
            <form method="post" [action]="externalLoginAction" class="mb-2">
              <input type="hidden" name="provider" [value]="provider.name" />
              <input type="hidden" name="returnUrl" [value]="resumeUrl() ?? ''" />
              <input type="hidden" name="__RequestVerificationToken" [value]="csrfToken()" />
              <button
                type="submit"
                [disabled]="!csrfToken() || !resumeUrl()"
                class="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                Continue with {{ provider.displayName }}
              </button>
            </form>
          }
        }
      </div>
    </div>
  `,
})
export class LoginComponent {
  private readonly accountLogin = inject(AccountLoginService);
  private readonly oidc = inject(OidcSecurityService);
  private readonly route = inject(ActivatedRoute);

  /**
   * Whose sign-in this is: a product's (from code, at once), a client's custom domain (from what it
   * uploaded, once the shared branding call answers), or the platform's. Null only while a custom
   * domain's answer is pending — the page then says plain "Sign in" rather than flashing ComputAll on
   * a customer's domain.
   */
  protected readonly brand = signal<LoginBrand | null>(null);

  protected readonly username = signal('');
  protected readonly password = signal('');
  protected readonly rememberMe = signal(false);
  protected readonly showPassword = signal(false);
  protected readonly submitting = signal(false);
  protected readonly errorMessage = signal('');
  protected readonly csrfToken = signal('');
  protected readonly providers = signal<ExternalProvider[]>([]);

  /** The root-relative /connect/authorize path to resume after an EXTERNAL login (extracted from the
   * returnUrl AuthServer handed us). Local login doesn't need it — oidc.authorize() re-derives it. */
  protected readonly resumeUrl = signal<string | null>(null);

  // authApiBaseUrl, not authority — same reason as AccountLoginService. This is a top-level form POST
  // that ends in AuthServer setting the SSO cookie; sending it to the canonical host while the user is
  // on a custom domain would set that cookie on the wrong hostname, and the /connect/authorize that
  // follows (on this origin) would never see it. It is also antiforgery-protected, and that cookie is
  // host-only too, so this POST must stay same-origin.
  //
  // AuthServer may then bounce the browser to the canonical host and back — the Google/Microsoft leg
  // is pinned there so the provider only ever needs ONE registered redirect URI (see
  // AuthServer/Services/CanonicalAuthHost). That is invisible from here, and deliberately does NOT
  // change where this POST goes: the round-trip ends with a hand-off that re-establishes the session
  // on THIS origin, which is the only place it is any use.
  protected readonly externalLoginAction = `${environment.authApiBaseUrl}/Account/ExternalLogin`;

  constructor() {
    // The tab says whose sign-in this is while the page is open, and is put back on the way out: no
    // route sets a title, so nothing else would, and the next page (forgot password, say) would keep
    // saying "Sign in".
    const title = inject(Title);
    const previous = title.getTitle();
    inject(DestroyRef).onDestroy(() => title.setTitle(previous));

    const product = productBrandFor(inject(SERVED_HOSTNAME));
    const show = (brand: LoginBrand) => {
      this.brand.set(brand);
      title.setTitle(`${brand.name} - Sign in`);
    };

    if (product) {
      show(loginBrandFor(product, null, environment.apiBaseUrl));
    } else {
      void inject(HOST_BRANDING).then((branding) => show(loginBrandFor(null, branding, environment.apiBaseUrl)));
    }

    // AuthServer redirects a FAILED external login back here as ?error=<code> (not_linked, locked_out,
    // external_error). Surface it so the user sees why, instead of a blank form.
    const error = this.route.snapshot.queryParamMap.get('error');
    if (error) {
      this.errorMessage.set(this.messageFor(new LoginError(error)));
    }

    const fromParam = this.extractResumeUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
    if (fromParam) {
      this.resumeUrl.set(fromParam);
    } else {
      // Reached /login directly, with no authorize URL to resume. Generate one via the oidc library
      // (which persists a matching PKCE verifier/state/nonce) so external login has a returnUrl that
      // (a) carries client_id=core-web-spa, letting the backend route its FAILURES back to this SPA
      // page (IsSpaReturnUrl in AuthServer/Account.cs) instead of the Razor login, and (b) is a real,
      // resumable authorize request on SUCCESS. Root-relative so Url.IsLocalUrl accepts it.
      void firstValueFrom(this.oidc.getAuthorizeUrl()).then((url) => {
        if (url) {
          this.resumeUrl.set(this.toRootRelative(url));
        }
      });
    }

    void this.accountLogin
      .getAntiforgeryToken()
      .then((token) => this.csrfToken.set(token))
      .catch(() => this.errorMessage.set('Could not reach the sign-in service. Try again.'));

    void this.accountLogin
      .getExternalProviders()
      .then((providers) => this.providers.set(providers))
      .catch(() => {
        /* External providers are optional; a failure just hides the buttons. */
      });
  }

  protected async submit(event: Event): Promise<void> {
    event.preventDefault();
    if (this.submitting()) {
      return;
    }

    // A browser that autofills a saved login can do it without the (input) events the signals listen
    // to (card 192), so take what is actually in the fields rather than posting an empty password.
    const fields = (event.target as HTMLFormElement).elements;
    this.username.set((fields.namedItem('username') as HTMLInputElement).value);
    this.password.set((fields.namedItem('password') as HTMLInputElement).value);

    this.errorMessage.set('');
    this.submitting.set(true);
    try {
      await this.accountLogin.login(
        this.username(),
        this.password(),
        this.rememberMe(),
        this.csrfToken(),
      );
      // ANOTHER CLIENT'S SIGN-IN IS HANDED BACK, not replaced. Since card 170 the Batch Billing desktop's
      // browser sign-in lands here too, and starting the SPA's own flow would sign the person in to the
      // SPA while the desktop waited for a code that never came.
      const foreign = resumableForeignAuthorize(this.resumeUrl(), window.location.origin);
      if (foreign) {
        window.location.assign(foreign);
        return;
      }

      // Cookie is set; re-enter the OIDC flow. This is a full-page redirect to /connect/authorize,
      // which now issues a code → /callback restores the original deep link. Keep submitting() true
      // so the button stays disabled through the navigation.
      this.oidc.authorize();
    } catch (error) {
      this.errorMessage.set(this.messageFor(error));
      this.submitting.set(false);
    }
  }

  /**
   * Card 192: "Show" did nothing on the first click; Hide→Show worked. An autofilled password is in the
   * field but not in password(), so swapping the type re-rendered it from an empty signal. Read the
   * field first (the click is a user gesture, so Chrome now exposes an autofilled value), then put the
   * value back after the swap in case the browser dropped its autofill preview.
   */
  protected togglePassword(input: HTMLInputElement): void {
    const value = input.value;
    this.password.set(value);
    this.showPassword.set(!this.showPassword());
    input.type = this.showPassword() ? 'text' : 'password';
    input.value = value;
  }

  private messageFor(error: unknown): string {
    const code = error instanceof LoginError ? error.code : 'unknown';
    switch (code) {
      case 'invalid_credentials':
        return 'Incorrect username or password.';
      case 'locked_out':
        return 'This account is locked. Try again later.';
      // "Manage → External logins" was the old server-rendered page; the SPA's is in the user menu.
      case 'not_linked':
        return "That Google account isn't linked to a user yet. Sign in with your username and password, open your user menu → External Logins and press Link, then use Continue with Google next time.";
      case 'external_cancelled':
        return 'Sign-in was cancelled at the provider. Choose an account to continue, or sign in with your password.';
      case 'external_error':
        return 'The external provider could not sign you in. Please try again; if it keeps failing, sign in with your password.';
      default:
        return 'Something went wrong signing you in. Please try again.';
    }
  }

  /** AuthServer hands us its Razor login URL with the real authorize path nested as ?ReturnUrl=…. */
  private extractResumeUrl(loginReturnUrl: string | null): string | null {
    if (!loginReturnUrl) {
      return null;
    }
    try {
      return new URL(loginReturnUrl, window.location.origin).searchParams.get('ReturnUrl');
    } catch {
      return null;
    }
  }

  /** Strip the origin from an absolute authorize URL so it is a local (root-relative) returnUrl. */
  private toRootRelative(url: string): string {
    try {
      const parsed = new URL(url, window.location.origin);
      return `${parsed.pathname}${parsed.search}`;
    } catch {
      return url;
    }
  }
}

/**
 * The authorize request to resume after a local login, when it belongs to a client OTHER than this
 * SPA — or null, meaning "start the SPA's own flow".
 *
 * Only a same-origin `/connect/authorize` path is accepted, so the query string AuthServer handed us
 * cannot be turned into an open redirect. The request itself is still validated by OpenIddict when it
 * is resumed (client, redirect_uri, PKCE), so this decides only WHERE to go, never what is granted.
 */
export function resumableForeignAuthorize(resumeUrl: string | null, origin: string): string | null {
  if (!resumeUrl) {
    return null;
  }
  try {
    const parsed = new URL(resumeUrl, origin);
    const clientId = parsed.searchParams.get('client_id');
    if (
      parsed.origin !== origin ||
      !parsed.pathname.endsWith('/connect/authorize') ||
      !clientId ||
      clientId === environment.clientId
    ) {
      return null;
    }
    return `${parsed.pathname}${parsed.search}`;
  } catch {
    return null;
  }
}
